// Interfaces
export interface BlogPost {
    id: string;
    title: string;
    excerpt: string;
    content: string;
    author: string;
    date: string;
    readTime: string;
    tags: string[];
    image?: string;
}

export interface NewsArticle {
    id: string;
    title: string;
    excerpt: string;
    content: string;
    date: string;
    source: string;
    category: string;
}

export interface TeamMember {
    id: string;
    name: string;
    role: string;
    bio: string;
    title?: string;
    image?: string;
}

export interface Service {
    id: string;
    title: string;
    description: string;
    features: string[];
    icon: string;
}

export interface CaseStudy {
    id: string;
    title: string;
    client: string;
    industry: string;
    challenge: string;
    solution: string;
    results: string[];
    technologies: string[];
}

// ============================================
// ORIGINAL STATIC CONTENT
// ============================================

export const aboutContent = {
    title: "About Mercury Technology Solutions",
    tagline: "Architecting Digital Authority in the Age of AI",
    subtitle: "Accelerate Digitality.",
    whoWeAre: {
        title: "Who We Are",
        paragraphs: [
            "Founded in 2017, Mercury Technology Solutions is a premier digital transformation partner dedicated to a single mission: to Accelerate Digitality for forward-thinking enterprises. We do not just build software; we build the \"Trust Layer\" that modern businesses need to survive and thrive.",
            "In a world where your customers' first interaction with you often happens silently on a screen—or via an AI assistant—your digital reputation is your most valuable asset. We empower brands to move beyond simple marketing and instead \"Architect Digital Authority,\" ensuring that when the world asks questions about your industry, you are the definitive answer."
        ]
    },
    philosophy: {
        title: "The Mercury Philosophy: Built in Code",
        intro: "For over a century, trust was built in stone—in physical branches and face-to-face handshakes. Today, trust is built in code.",
        belief: "We believe that to lead in the new era, you must master the three pillars of digital visibility:",
        pillars: [
            { name: "Foundational Authority (SEO)", description: "Building your \"digital flagship branch\" through long-term search dominance." },
            { name: "Immediate Engagement (SEM)", description: "Capturing high-intent opportunities the moment they arise." },
            { name: "Generative AI Optimization (GAIO)", description: "The new discipline of shaping expert conversations so that AI assistants (like ChatGPT and Gemini) cite you as the primary source." }
        ]
    },
    solutions: {
        title: "Our Integrated Solutions Ecosystem",
        intro: "We provide a comprehensive \"Integrated Solutions Suite\" designed to streamline operations and elevate marketing effectiveness. Our technology stack is divided into two strategic engines:",
        engines: [
            {
                name: "The Operational Engine (Business Efficiency)",
                products: [
                    { name: "Mercury Business Operation Suite (ERP)", description: "A fully functional ERP streamlining sales, purchasing, expenses, and HR to optimize business processes." },
                    { name: "Mercury SocialHub CRM", description: "A centralized platform for managing leads, events, and ticketing while integrating social media and email marketing." },
                    { name: "Mercury PartnerPlus", description: "An advanced system to manage reseller relationships, affiliates, and automated commission structures." },
                    { name: "Amalgam Membership System", description: "A dynamic platform for subscription management, digital goods, and referral programs, available in both Web and Telegram UX." }
                ]
            },
            {
                name: "The Growth Engine (Digital Authority)",
                products: [
                    { name: "Mercury CMS + SEO Automation", description: "A robust content management system with built-in SEO tools, schema markup generation, and Google Indexing protocols." },
                    { name: "Mercury ContentFlow AI Suite", description: "Revolutionizing e-commerce with Muses AI, capable of generating bulk product descriptions and localizing content into 95+ languages." },
                    { name: "Mercury SEVO (Search Everywhere Optimization)", description: "A holistic service that goes beyond Google to optimize your presence across social media, voice search, and AI platforms." },
                    { name: "Mercury LLM-SEO Services (GAIO)", description: "Strategic optimization to ensure your brand is trusted, cited, and recommended by Large Language Models (LLMs)." },
                    { name: "Mercury Phygital Solutions", description: "Bridging the physical and digital worlds with people-counting technology and unified customer messaging." }
                ]
            }
        ]
    },
    standard: {
        title: "Our Standard: A.C.C.U.R.A.T.E.",
        intro: "We do not just value quality; we operationalize it. All our content and strategies adhere to the A.C.C.U.R.A.T.E. standard—our \"north star\" for quality in the age of AI.",
        items: [
            { letter: "A", term: "Auditable", description: "We track creation and modification." },
            { letter: "C", term: "Compliant", description: "We meet strict legal and industry regulations." },
            { letter: "C", term: "Consistent", description: "We align with your unique brand voice." },
            { letter: "U", term: "Unified", description: "We ensure cohesive assets across your ecosystem." },
            { letter: "R", term: "Reviewed", description: "We maintain a \"human-in-the-loop\" for fact-checking." },
            { letter: "A", term: "Authoritative", description: "We demonstrate verifiable E-E-A-T (Experience, Expertise, Authoritativeness, Trustworthiness)." },
            { letter: "T", term: "Traceable", description: "We identify sources and prompts." },
            { letter: "E", term: "Ethical", description: "We are transparent and bias-free." }
        ]
    },
    mission: "\"To empower businesses to become the Source of the Answer.\" We help you build a deep, verifiable \"Trust Layer\" around your brand, ensuring you are no longer just an option on a list, but the primary, trusted source for both human customers and AI assistants.",
    stats: [
        { label: "Years of Architecting Digital Authority", value: "7+" },
        { label: "Digital Transformation Projects Delivered", value: "200+" },
        { label: "Clients Empowered to \"Accelerate Digitality\"", value: "150+" },
        { label: "Experts in AI, SEO, and Software Development", value: "50+" },
        { label: "SKUs Managed via Mercury ContentFlow AI", value: "20,000+" },
        { label: "ROAS achieved in SEM Campaigns", value: "600%" }
    ]
};

export const servicesContent = {
    title: "Our Integrated Solutions Suite",
    subtitle: "Architecting the Infrastructure of Digital Trust",
    tagline: "Accelerate Digitality.",
    intro: "We do not just offer services; we deploy a comprehensive Integrated Solutions Suite designed to empower brands to succeed in a digital world. From optimizing your internal operations to ensuring your brand is the \"source of the answer\" in AI search, our ecosystem covers every touchpoint of the modern digital journey.",
    categories: [
        {
            icon: "🤖",
            title: "Intelligent Innovation & AI Agents",
            description: "Leverage the power of the Mercury Muses AI and custom integrations to automate processes and create intelligent experiences.",
            services: [
                {
                    name: "Mercury Muses AI (Intelligent Assistant)",
                    description: "An advanced AI agent that automates content creation, enhances SEO, and acts as a personal operational assistant. It creates high-quality blog content, optimizes meta tags, and even identifies sales follow-up items."
                },
                {
                    name: "Customized A.I. Integration",
                    description: "Beyond standard tools, we develop bespoke AI models, machine learning algorithms, and NLP applications tailored to your unique operational challenges."
                },
                {
                    name: "Customized Blockchain Solutions",
                    description: "Tailored integration services for supply chain management, secure data sharing, and tokenization to ensure transparency and security."
                }
            ]
        },
        {
            icon: "📈",
            title: "Digital Authority & SEVO (Search Everywhere Optimization)",
            description: "Move beyond traditional marketing. Build a \"Trust Layer\" that dominates organic search and Generative AI answers.",
            services: [
                {
                    name: "Mercury LLM-SEO Services (GAIO)",
                    description: "A strategic service designed to optimize your online presence for Large Language Models (LLMs). We focus on E-E-A-T amplification and reputation monitoring to ensure AI chatbots (like ChatGPT and Gemini) recognize and trust your brand."
                },
                {
                    name: "Mercury SEVO (Search Everywhere Optimization)",
                    description: "A holistic approach that addresses the fragmented customer journey across social media, video, and AI, not just Google. We identify platform-specific keywords and execute cross-platform content strategies."
                },
                {
                    name: "Mercury CMS + SEO Automation",
                    description: "A robust platform with built-in SEO recommendations, automatic sitemap generation, and Google Indexer mechanisms to drive organic traffic."
                },
                {
                    name: "Mercury ContentFlow AI Suite",
                    description: "Revolutionize your e-commerce with intelligent bulk content creation that manages large catalogs (20,000+ SKUs) and ensures brand consistency."
                }
            ]
        },
        {
            icon: "💻",
            title: "Enterprise Operations & Ecosystems",
            description: "Streamline workflows and build robust digital infrastructures with our fully functional enterprise software.",
            services: [
                {
                    name: "Mercury Business Operation Suite (ERP)",
                    description: "A comprehensive ERP streamlining Sales, Purchasing, Expense Claims, and HR into a single, efficient module."
                },
                {
                    name: "Mercury SocialHub CRM",
                    description: "A centralized platform for managing leads, executing email marketing, and handling event ticketing, integrated with major social media platforms."
                },
                {
                    name: "Mercury PartnerPlus",
                    description: "An advanced dashboard for managing reseller relationships, tracking affiliate performance, and automating commission payouts."
                },
                {
                    name: "Amalgam Membership System",
                    description: "A dynamic platform (Web & Telegram UX) for managing subscriptions, selling digital goods, and fostering community engagement."
                }
            ]
        },
        {
            icon: "🎯",
            title: "Phygital Experience & Strategic Consulting",
            description: "Strategic guidance to bridge the gap between physical engagement and digital strategy.",
            services: [
                {
                    name: "Mercury Phygital Solutions",
                    description: "We integrate physical and digital experiences using people-counting technology and unified messaging to optimize customer journeys in physical locations."
                },
                {
                    name: "Digital Transformation Strategy",
                    description: "We utilize the A.C.C.U.R.A.T.E. Standard to audit your content and operations, ensuring you build a defensible \"Trust Layer\" around your brand."
                },
                {
                    name: "The A.C.I.D. Sprint",
                    description: "An agile framework to rapidly build authority, citations, and infrastructure for high-value topics."
                }
            ]
        }
    ]
};

export const teamContent = {
    title: "The Architects of Digital Authority",
    subtitle: "Leadership Team",
    tagline: "Accelerate Digitality.",
    intro: "We are a collective of strategists, engineers, and financial leaders united by a single mission: to build the \"Trust Layer\" for the AI era. Our leadership team bridges the gap between traditional business integrity and the frontier of Generative AI.",
    members: [
        {
            id: "james-huang",
            name: "James Huang",
            role: "CEO & Founder",
            title: "The Visionary Behind the Framework",
            bio: "With over 20 years of experience in high-stakes digital transformation, James founded Mercury with a clear directive: to democratize Artificial Intelligence for the modern enterprise."
        },
        {
            id: "kwok-wing-chan",
            name: "Kwok-Wing Chan",
            role: "Project Implementation and Management Director",
            title: "The Engine of Execution",
            bio: "Bridging the gap between complex theory and operational reality, Kwok-Wing oversees the deployment of our Integrated Solutions Suite."
        }
    ]
};

// ============================================
// RESTORED MISSING EXPORTS
// ============================================

export const technologyContent = {
    title: "Our Technology Stack",
    subtitle: "The Architecture of Digitality",
    tagline: "Accelerate Digitality.",
    intro: "We do not just write code; we build the \"Trust Layer\" for modern business. Our engineering philosophy combines battle-tested enterprise infrastructure with the cutting-edge agility of Generative AI. This is the foundational technology that powers the Mercury Integrated Solutions Suite, ensuring your business is scalable, secure, and future-proof.",
    categories: [
        {
            icon: "🧠",
            title: "The Intelligence Engine (AI & ML)",
            description: "Powering Mercury Muses AI and ContentFlow AI Suite. We orchestrate the world's most advanced Large Language Models to drive our GAIO (Generative AI Optimization) strategies.",
            technologies: [
                { name: "Foundation Models", description: "We utilize Gemini 3.0, OpenAI GPT-5 and Claude to power the creative reasoning behind Muses AI, enabling human-like content creation and deep relevance analysis." },
                { name: "Orchestration", description: "LangChain serves as the connective tissue, allowing our ContentFlow AI Suite to manage complex workflows and localize content into 95+ languages dynamically." },
                { name: "Custom Training", description: "We deploy TensorFlow and PyTorch to build custom machine learning models for predictive analytics and specialized Customized A.I. Integration Solutions." }
            ]
        },
        {
            icon: "⚙️",
            title: "Enterprise Core & ERP",
            description: "Powering the Mercury Business Operation Suite. We build upon proven global standards to ensure operational stability and financial accuracy.",
            technologies: [
                { name: "Odoo Framework", description: "The Mercury Business Operation Suite is architected on the robust, modular Odoo engine. This provides the stable foundation for our comprehensive ERP modules—including Sales, Purchasing, and Accounting—ensuring enterprise-grade reliability." },
                { name: "PostgreSQL", description: "We utilize PostgreSQL to handle the complex relational data required for precise financial reporting and inventory tracking, ensuring ACID compliance and data integrity." }
            ]
        },
        {
            icon: "🎨",
            title: "Digital & Phygital Interfaces",
            description: "Delivering the Mercury CMS, Phygital Solutions, and Immersive Web. We build high-performance, reactive front-ends that bridge the gap between digital interaction and physical engagement.",
            technologies: [
                { name: "Immersive Animation", description: "We deploy GSAP (GreenSock Animation Platform) in tandem with Three.js to craft cinematic, scroll-based animations. This allows our Phygital Solutions to deliver \"wow\" moments and visual storytelling that feel alive." },
                { name: "Core Frameworks", description: "React and Next.js drive the lightning-fast, SEO-optimized interfaces of our Listing/Directory Solutions and CMS." },
                { name: "Language Standard", description: "We strictly use TypeScript to ensure the code reliability required for scalable applications." },
                { name: "Server-Side", description: "Node.js powers our real-time back-end services, handling the high-concurrency demands of the Kaon Messaging Platform." }
            ]
        },
        {
            icon: "☁️",
            title: "Cloud & Edge Infrastructure",
            description: "Ensuring the scalability of Mercury PartnerPlus and SocialHub CRM. We operate an exclusive, high-performance cloud architecture designed for speed and global availability.",
            technologies: [
                { name: "Google Cloud Platform (GCP)", description: "We rely exclusively on Google Cloud to host our mission-critical ERP, CRM, and AI workloads, leveraging its superior data analytics and machine learning infrastructure." },
                { name: "The Edge", description: "We combine Vercel and Cloudflare to push our content and applications to the network edge. This ensures that whether a user is in Hong Kong, Singapore, or Jakarta, your digital presence loads instantly, critical for maintaining Core Web Vitals." },
                { name: "Containerization", description: "Docker and Kubernetes allow us to deploy and scale our Microservices Architecture on GCP, ensuring consistent environments from development to production." }
            ]
        },
        {
            icon: "🔍",
            title: "Data, Search & Custom Solutions",
            description: "Driving Mercury SEVO, Listing Solutions, and Custom Apps. We use modern, intelligent data tools to architect the \"Trust Layer\" and provide lightning-fast information retrieval.",
            technologies: [
                { name: "Meilisearch", description: "We utilize Meilisearch to power the search intelligence within our Listing/Directory Solutions and Mercury CMS. Its typo-tolerant, ultra-fast search engine ensures that users (and AI agents) find the exact answer they need instantly." },
                { name: "Prisma ORM", description: "For our Custom Web Applications, we rely on Prisma as our modern database toolkit. It provides type-safe database access, ensuring our custom solutions are built with fewer errors and maximum efficiency when interacting with our data layer." },
                { name: "Redis", description: "We use Redis for real-time caching and session management, ensuring high performance for the Kaon Messaging Platform." }
            ]
        }
    ]
};

export const careersContent = {
    title: "Careers at Mercury",
    subtitle: "Accelerate Digitality. Accelerate Your Career.",
    tagline: "Accelerate Digitality.",
    intro: {
        title: "Build the \"Trust Layer\" of the AI Era",
        paragraphs: [
            "At Mercury Technology Solutions, we are not just coding software; we are architecting the future of digital authority. In an era where trust is the ultimate currency , we are building the \"Trust Layer\" that defines how humans and AI assistants interact.",
            "We are looking for visionaries who want to move beyond traditional tech and help global brands become \"the source of the answer\". If you are ready to master the new language of digital visibility—from GAIO to Phygital Solutions—you belong here."
        ]
    },
    whyJoin: {
        title: "Why Join the Mercury Ecosystem?",
        intro: "We believe in empowering our people just as we empower our clients.",
        benefits: [
            { icon: "⚡", title: "Innovate at the Edge", description: "Work with proprietary frameworks like the A.C.C.U.R.A.T.E. Standard and Muses AI." },
            { icon: "🌍", title: "Remote-First, Global Mindset", description: "Whether you are in Hong Kong, Singapore, or remote, we focus on output and impact, not hours in a chair." },
            { icon: "📚", title: "Unlimited Learning Budget", description: "The digital landscape changes fast. We fund your continuous learning to ensure you stay ahead of trends like Generative AI Optimization (GAIO)." },
            { icon: "❤️", title: "Comprehensive Wellness", description: "We support your physical and mental well-being so you can perform at your peak." },
            { icon: "💰", title: "Competitive Wealth Building", description: "Top-tier salary packages and equity opportunities that reward your contribution to our growth." },
            { icon: "🎉", title: "Legendary Retreats", description: "We gather annually to celebrate our wins, share knowledge, and plan the future of the Mercury Integrated Solutions Suite." }
        ]
    },
    openings: [
        { title: "Senior Frontend Engineer", location: "Global / Remote", type: "Full-time" },
        { title: "AI Research Scientist", location: "Hong Kong", type: "Full-time" },
        { title: "Digital Marketing Manager", location: "London", type: "Full-time" }
    ]
};

export const contactInfo = {
    title: "Connect with Mercury",
    subtitle: "Ready to Accelerate Digitality?",
    tagline: "Don't just be an option. Become the answer.",
    intro: [
        "In a digital landscape defined by AI and rapid change, your brand needs more than just a website—it needs a verifiable \"Trust Layer\". Whether you are looking to streamline complex workflows with the Mercury Business Operation Suite (ERP) or ensure your brand is recommended by AI assistants via Generative AI Optimization (GAIO), our team is ready to architect your solution.",
        "Reach out today to future-proof your brand and secure your leadership position for the next generation of customers."
    ],
    locationsTitle: "Global Network & Strategic Hubs",
    locationsSubtitle: "We operate seamlessly across the physical and digital worlds, bridging research with execution.",
    locations: [
        { city: "Hong Kong", tagline: "Global HQ", address: "Building Legacy on Innovation", focus: "Global Strategy, Mercury SEVO Services, & Phygital Solutions." },
        { city: "United Kingdom", tagline: "Research Office", address: "The Forge of Future Intelligence", focus: "R&D, Muses AI Model Training, & Blockchain Integration Research." },
        { city: "Japan", tagline: "Consulting Services Hub", address: "Bridging Tradition & Digitality", focus: "Generative AI Optimization (GAIO) & Advanced Phygital Implementation." },
        { city: "Singapore", tagline: "Consulting Services Hub", address: "The Gateway to Southeast Asia", focus: "Mercury SocialHub CRM & Enterprise ERP Integration." }
    ],
    contactMethods: {
        title: "Start the Conversation",
        email: { address: "hello@mtsoln.com", note: "For inquiries regarding Partnership Opportunities or Media, please specify in the subject line." },
        phone: { number: "+852 6630 3083", note: "Speak directly with a Digital Architect about your Customized A.I. Integration needs." }
    },
    nextSteps: {
        title: "What happens next?",
        steps: [
            { name: "Discovery", description: "We analyze your current digital footprint and operational bottlenecks." },
            { name: "Strategy", description: "We propose a tailored blueprint—from LLM-SEO audits to Blockchain Integration." },
            { name: "Execution", description: "We deploy the Mercury Integrated Solutions Suite to drive measurable growth." }
        ]
    }
};

export const blogPosts: BlogPost[] = [];

export const newsArticles: NewsArticle[] = [
    {
        id: "pan-asian-expansion",
        title: "Mercury Technology Solutions Accelerates Pan-Asian Expansion with Strategic Hubs in Singapore and Jakarta",
        excerpt: "Building on its successful March 2024 launch in Japan, Mercury deploys its \"Trust Layer\" framework to Southeast Asia’s booming Retail and Financial sectors.",
        content: `
            <p><strong>HONG KONG, December 8, 2024</strong> — Mercury Technology Solutions, a premier digital transformation partner, today announced a major strategic expansion into Southeast Asia with the opening of new operational hubs in Singapore and Jakarta. This move follows the company’s successful entry into Japan in March 2024, further solidifying its mission to "Accelerate Digitality" across the APAC region.</p>
            
            <p>As the digital landscape shifts from traditional search to AI-driven answers, businesses across Asia face a critical need to evolve. Following the successful deployment of its proprietary frameworks in Tokyo earlier this year, Mercury’s expansion is designed to help Southeast Asian organizations architect a "Trust Layer" around their brands. This ensures they become the definitive "source of the answer" for both human customers and AI assistants like ChatGPT and Gemini.</p>

            <p>"2024 has been a transformative year for Mercury, starting with the opening of our Japan office in March and culminating in this strategic entry into Southeast Asia," said James Huang, CEO of Mercury Technology Solutions. "Our operations in Tokyo proved that even the most traditional markets are ready to embrace Generative AI Optimization (GAIO) and Phygital Solutions. We are now bringing that battle-tested expertise to Singapore and Jakarta to empower local enterprises to transition their legacy of trust into the digital realm."</p>

            <h3>Empowering Key Sectors with Integrated Solutions</h3>
            <p>The new Singapore and Jakarta offices will replicate the "Tokyo Blueprint," deploying the Mercury Integrated Solutions Suite to transform operations for clients in three core sectors:</p>
            
            <ul>
                <li><strong>Retail:</strong> Leveraging Mercury Phygital Solutions —which has seen strong adoption in Japan’s high-density retail environments—to bridge the gap between in-store engagement and online interactions. Retailers will utilize people-counting technology and the Kaon Messaging Platform to unify customer experiences across WhatsApp, LINE, and physical touchpoints.</li>
                <li><strong>Financial Services:</strong> Deploying Mercury LLM-SEO Services (GAIO) to help institutions demonstrate E-E-A-T (Experience, Expertise, Authoritativeness, and Trustworthiness). This ensures financial brands maintain their reputation as trusted authorities when customers seek advice from AI assistants.</li>
                <li><strong>Technology & E-commerce:</strong> Utilizing the Mercury ContentFlow AI Suite and Muses AI to manage large product catalogs (20,000+ SKUs) and automate multi-lingual content creation, ensuring rapid scalability across the diverse languages of Southeast Asia.</li>
            </ul>

            <h3>Rapid Growth Driven by AI Demand</h3>
            <p>This expansion comes on the heels of significant momentum. Mercury Technology Solutions has reported a 150% year-over-year growth in demand for its AI consulting and LLM-SEO services. This surge, fueled by the combined output of the Hong Kong, Tokyo, and new Southeast Asian teams, reflects the urgent market need for the Mercury SEVO (Search Everywhere Optimization) framework.</p>

            <p>To support this regional growth, Mercury plans to expand its local specialist teams to over 8 employees by Q3 2026, recruiting top talent in AI engineering and digital strategy.</p>

            <h3>About Mercury Technology Solutions</h3>
            <p>Mercury Technology Solutions empowers brands to significantly improve business operations, elevate marketing effectiveness, and boost overall efficiency through the strategic implementation of cutting-edge technology. From the Mercury Business Operation Suite (ERP) to the Mercury SocialHub CRM, we develop software for customers to succeed in a digital world.</p>

            <p>For more information, please visit <a href="https://mtsoln.com">https://mtsoln.com</a></p>
        `,
        date: "Dec 08, 2024",
        source: "Press Release",
        category: "Expansion News"
    }
];
